/**
 * Composition root of the SPA.
 *
 * Order matters and is deliberate:
 * 1. `QueryClientProvider` — everything below may fetch;
 * 2. `ErrorBoundary` — a render failure in a page must not blank the app;
 * 3. `DirectionProvider` and `ThemeProvider` — document-level concerns, applied before content renders.
 *
 * The query client is created once per application instance through lazy state; creating it during render (or in
 * module scope) would either leak a cache between tests or share one across users of the same tab.
 */

import { QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';

import { DirectionProvider } from '@/providers/DirectionProvider';
import { ErrorBoundary } from '@/providers/ErrorBoundary';
import { ThemeProvider } from '@/providers/ThemeProvider';
import { createQueryClient } from '@/lib/queryClient';

/** Props accepted by {@link AppProviders}. */
export interface AppProvidersProps {
  /** Application tree. */
  children: React.ReactNode;
}

/** Wraps the application in every global provider. */
export function AppProviders({ children }: AppProvidersProps) {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <DirectionProvider>
          <ThemeProvider>{children}</ThemeProvider>
        </DirectionProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}
